import { EditorState, Modifier } from "draft-js";

/**
 * Plain-text length of the current selection (for replace-on-type / paste).
 */
export const getSelectedPlainTextLength = (editorState) => {
  const selection = editorState.getSelection();
  if (selection.isCollapsed()) {
    return 0;
  }

  const contentState = editorState.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();

  if (startKey === endKey) {
    return endOffset - startOffset;
  }

  let length = 0;
  let inSelection = false;

  contentState.getBlockMap().forEach((block, blockKey) => {
    if (blockKey === startKey) {
      inSelection = true;
      length += block.getText().length - startOffset;
    } else if (blockKey === endKey) {
      length += endOffset;
      inSelection = false;
    } else if (inSelection) {
      length += block.getText().length;
    }
  });

  return length;
};

/**
 * Allow at most maxLength chars. When already over max (e.g. loaded edit or lower max after logo),
 * still allow deletes and replacements that do not grow total length.
 */
export const shouldAcceptWelcomeLetterPlainTextLengthChange = (
  currentLength,
  nextLength,
  maxLength
) => {
  if (nextLength <= maxLength) {
    return true;
  }
  return nextLength <= currentLength;
};

export const getProjectedPlainTextLengthAfterInsert = (
  editorState,
  insertedText = ""
) => {
  const currentLength = editorState.getCurrentContent().getPlainText().length;
  const selectedLength = getSelectedPlainTextLength(editorState);
  return currentLength - selectedLength + insertedText.length;
};

/**
 * Insert plain text at selection, truncated to fit maxLength (after removing selected text).
 * Returns new EditorState or null if nothing can be inserted.
 */
export const insertPlainTextWithMaxLength = (
  editorState,
  text,
  maxLength
) => {
  const currentLength = editorState.getCurrentContent().getPlainText().length;
  const selectedLength = getSelectedPlainTextLength(editorState);
  const baseLength = currentLength - selectedLength;
  const remaining = maxLength - baseLength;

  if (remaining <= 0) {
    return null;
  }

  const truncated = text.slice(0, remaining);
  if (!truncated) {
    return null;
  }

  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const newContent = Modifier.replaceText(
    contentState,
    selection,
    truncated,
    editorState.getCurrentInlineStyle()
  );

  return EditorState.push(editorState, newContent, "insert-characters");
};
